import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';
import difference from 'lodash/difference';
import moment from 'moment';

interface DeliveryPointFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    form: any;
    schema: any;

    savedZones: any[];
    allZones: any[];

    meta: any;
}

const state: DeliveryPointFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    form: null,
    schema: null,

    savedZones: [],
    allZones: [],

    meta: {},
};

const initState: DeliveryPointFormState = cloneDeep(state);

export const DELIVERY_POINT_DATA = 'GET_DELIVERY_POINT_DATA';
export const DELIVERY_POINT_SCHEMA = 'GET_DELIVERY_POINT_SCHEMA';
export const GET_LINKED_ZONES = 'GET_LINKED_ZONES';

export const actions: ActionTree<DeliveryPointFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(DELIVERY_POINT_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),

                vm.$q(GET_LINKED_ZONES)
                    .select()
                    .from('delivery_points_delivery_zones')
                    .fileds({
                      id: 'delivery_points_delivery_zones.id',
                      delivery_point_id: 'delivery_points_delivery_zones.delivery_point_id',
                      delivery_zone_id: 'delivery_points_delivery_zones.delivery_zone_id',
                    })
                    .where((_: any) => {
                      return _.eq('delivery_points_delivery_zones.delivery_point_id', _.const(params.id));
                    })
                    .limit(1000)
            ]).then((result: ServerResult) => {
                const pointData = result.getOperation(DELIVERY_POINT_DATA).getContent();
                commit('SET_FORM_DATA', {
                    ...pointData,
                    zones: result.getOperation(GET_LINKED_ZONES).getContent().documents
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(DELIVERY_POINT_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:delivery_points`
                    }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    ...result.getOperation(DELIVERY_POINT_SCHEMA).getContent(),
                    initParams
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },
    addZone({ commit, state }) {
        state.allZones.push({
            id: null,
            delivery_zone_id: null,
        })
    },
    removeZone({ commit, state }, index) {
        state.allZones.splice(index, 1);
    },
    changeZoneValue({ commit, state }, {index, key, newValue}) {
        state.allZones[index][key] = newValue
    },

    saveForm({ commit, state }, documentId) {
        const vm = (this as any)._vm;

        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            const newZones = state.allZones.filter((zone: any) => !zone.id);
            const updateZones = state.allZones.filter((zone: any) => zone.id);

            const removedIds = difference(
                state.savedZones.map((zone: any) => zone.id),
                state.allZones.map((zone: any) => zone.id),
            )

            console.log('newZones', newZones);
            console.log('updateZones', updateZones)
            console.log('removedIds', removedIds)

            if (documentId) {
                saveQuery = vm.$q('DELIVERY_POINT_DATA_SAVE')
                    .update(documentId, normalizedForm)
                    .createManyRelation('delivery_points_delivery_zones', 'delivery_point_id', (createRelated: any) => {
                        return newZones.map((zone: any, index: number) => createRelated(zone));
                      })
                      .updateManyRelation('delivery_points_delivery_zones', 'delivery_point_id', (updateRelated: any) => {
                        return updateZones.map((zone: any, index: number) => updateRelated(zone));
                      })
                      .deleteManyRelation('delivery_points_delivery_zones', 'delivery_point_id', (deleteRelated: any) => {
                        return removedIds.map((relId: any, index: number) => deleteRelated({
                          id: relId
                        }));
                      })
            } else {
                saveQuery = vm.$q('DELIVERY_POINT_DATA_SAVE')
                    .create('s:delivery_points', normalizedForm)
                    .withManyRelation('delivery_points_delivery_zones', 'delivery_point_id', (createRelated: any) => {
                        return newZones.map((zone: any, index: number) => createRelated(zone));
                      })
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('DELIVERY_POINT_DATA_SAVE');

                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', operationResult.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },

    setArchive({state, commit}) {
        return new Promise((resolve, reject) => {
            commit('START_LOADING');
            const vm = (this as any)._vm;

            const normalizedForm = prepareFormObjectForSave(state.schema, {
                archived_at: moment().toISOString()
            });

            const saveQuery = vm.$q('SAVE')
                .update(state.form.id, normalizedForm)
            vm.$db([
                saveQuery
            ]).then(((result: ServerResult) => {
                console.log(result)
                commit('LOADED_SUCCESS');
                resolve();
            }));
        })
    },

    setUnarchive({state, commit}) {
        return new Promise((resolve, reject) => {
            commit('START_LOADING');
            const vm = (this as any)._vm;

            const normalizedForm = prepareFormObjectForSave(state.schema, {
                archived_at: null
            });
            normalizedForm['archived_at'] = null;

            const saveQuery = vm.$q('SAVE')
                .update(state.form.id, normalizedForm)
            vm.$db([
                saveQuery
            ]).then(((result: ServerResult) => {
                console.log(result)
                commit('LOADED_SUCCESS');
                resolve();
            }));
        })
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

};

export const mutations: MutationTree<DeliveryPointFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_FORM_DATA(state, {document, schema, zones}) {
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.savedZones = zones;
        state.allZones = cloneDeep(zones);

        state.form = generateFormObject(schema, state.document);
    },

    GENERATE_FORM_DATA(state, {schema, initParams}) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, initParams);
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;

        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: DeliveryPointFormState, {field, newValue}) {
        switch (field) {
            default:
                state.form[field] = newValue;
        }
    },
};

const getters = {
    fields: (state: DeliveryPointFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_delivery_point_details_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
